/**
 * useMilestoneSync — ensure milestone storage is populated from bundled content on every startup.
 *
 * After bundled content is loaded this hook always reloads milestones from
 * AdminSegment 11255 (standard) and, in premium mode, AdminSegment 11270 (premium).
 *
 * Content resolution priority:
 *  1. `contentService` param when provided (API-backed, preferred — same path as DebugView)
 *  2. In-memory ContentStore records (bundled content loaded from assets)
 *
 * The hook re-runs when:
 *  - bundleStatus.isLoaded transitions to true (startup)
 *  - bundleStatus.version changes (re-downloaded bundle)
 *  - contentService becomes available for the first time (post-login)
 *
 * Works identically on mobile (ChildNav) and web (ChildNavWeb).
 */

import { useEffect, useRef } from 'react';
import type { BundledContentStatus } from './useBundledContent';
import { AdminSegmentStorage } from '../services/AdminSegmentStorage';
import { appConfig } from '../config/configManager';

/**
 * Module-level flag — reset to false on every cold app launch (module re-evaluation).
 * Prevents the hook from flooding AsyncStorage with repeated milestone writes when
 * React prop references change within the same session (e.g., stores useMemo update).
 * The flag does NOT block a second run triggered by a genuinely new contentService
 * (post-login API-backed service) — see secondSyncDone below.
 */
let initialSyncDoneThisSession = false;

export const useMilestoneSync = (
  storage: any,
  contentStore: any,
  bundleStatus: BundledContentStatus | null | undefined,
  contentService?: any
) => {
  const runningRef = useRef(false);
  // Track whether we've already completed a successful sync so we only
  // re-run when a NEW contentService arrives (not on every render).
  const lastContentServiceRef = useRef<any>(null);

  useEffect(() => {
    if (!bundleStatus?.isLoaded || !storage) return;
    if (runningRef.current) return;

    // Skip if nothing changed: same bundleStatus AND same contentService as last run
    const contentServiceChanged = contentService && contentService !== lastContentServiceRef.current;
    // Always run on first isLoaded signal; also re-run when contentService becomes available
    if (!contentStore && !contentService) return;

    const sync = async () => {
      runningRef.current = true;
      try {
        // CRITICAL: Pin the global storage reference to the adapter we are about
        // to write with.  MilestonesView.getSharedStorage() reads from
        // global.__childNavStorage; if that reference points to a plain-object
        // (e.g. the Zustand storage wrapper) the chunked data cannot be found.
        // Setting it here — before any write — guarantees read/write alignment.
        if (storage) {
          (globalThis as any).__childNavStorage = storage;
          console.log(`🔧 MilestoneSync: pinned global.__childNavStorage to ${storage?.constructor?.name}`);
        }

        const isFollowOnSync = contentService && contentService !== lastContentServiceRef.current;

        // SESSION GUARD: if we have already done both an initial sync AND a
        // follow-on (post-login API-backed) sync this session, skip any further
        // runs.  This prevents repeated large AsyncStorage writes caused by prop
        // reference churn from flooding the queue and delaying the Zustand
        // profile-persist write until after the user closes the app.
        if (initialSyncDoneThisSession && !isFollowOnSync) {
          console.log('🔧 MilestoneSync: initial sync already done this session — skipping duplicate run');
          return;
        }

        const bundleVersion: string = bundleStatus.version
          ?? (await storage.getItem('bundled_content_version'))
          ?? 'unknown';

        console.log(`🔄 MilestoneSync: loading milestones from bundled content (bundle v${bundleVersion})…`);

        // --- Resolve the content service to use ---
        // Priority 1: passed-in API-backed contentService (works after auth, same as DebugView)
        // Priority 2: thin wrapper that reads from the in-memory ContentStore (bundled assets)
        let resolvedContentService = contentService ?? null;

        if (!resolvedContentService && contentStore) {
          // Build a thin contentService that reads from the in-memory ContentStore
          resolvedContentService = {
            getContent: async (contentType: string, contentTypeId: string, languageTypeId: string) => {
              const query = { contentType, contentTypeId, languageTypeId };
              const storeState = contentStore.getState ? contentStore.getState() : null;
              if (storeState?.getContent) {
                const record = storeState.getContent(query);
                if (record?.tableData) {
                  console.log(`📦 MilestoneSync: found ${contentType} ${contentTypeId} in ContentStore`);
                  return record.tableData;
                }
              }
              // Fallback: direct records scan
              const state = typeof contentStore === 'function'
                ? contentStore((s: any) => s)
                : contentStore.getState?.();
              const records: any[] = state?.records ?? [];
              console.log(`🔍 MilestoneSync: scanning ${records.length} contentStore records for ${contentType} ${contentTypeId}`);
              const found = records.find((r: any) =>
                r.contentType === contentType &&
                r.contentTypeId === contentTypeId &&
                r.languageTypeId === languageTypeId
              );
              if (found?.tableData) {
                console.log(`📦 MilestoneSync: found ${contentType} ${contentTypeId} via records scan`);
                return found.tableData;
              }
              console.warn(`⚠️ MilestoneSync: ${contentType} ${contentTypeId} not found in ContentStore (${records.length} records)`);
              return null;
            }
          };
        }

        if (!resolvedContentService) {
          console.warn('⚠️ MilestoneSync: no content service available, skipping');
          return;
        }

        const adminSegmentStorage = new AdminSegmentStorage(storage);
        const isPremium = appConfig.isPremium();
        const languageId = '1';

        console.log(`📋 MilestoneSync: loading Standard milestones (AdminSegment 11255)…`);
        await adminSegmentStorage.reloadStandardMilestones(resolvedContentService, languageId);

        if (isPremium) {
          console.log(`📋 MilestoneSync: loading Premium milestones (AdminSegment 11270)…`);
          await adminSegmentStorage.reloadPremiumMilestones(resolvedContentService, languageId);
        }

        console.log(`📋 MilestoneSync: loading Concerns (AdminSegment 11272)…`);
        await adminSegmentStorage.reloadConcerns(resolvedContentService, languageId);

        // Record which contentService was used so we can detect future changes
        lastContentServiceRef.current = contentService ?? null;
        // Mark that we've completed at least one sync this session
        initialSyncDoneThisSession = true;

        // Signal any mounted MilestonesView / ConcernsView to refresh their data
        (globalThis as any).__milestoneStorageInvalidated = Date.now();
        (globalThis as any).__concernStorageInvalidated = Date.now();

        console.log(`✅ MilestoneSync: milestones + concerns loaded from bundled content (v${bundleVersion})`);
      } catch (err) {
        console.error('❌ MilestoneSync: failed to load milestones from bundled content:', err);
      } finally {
        runningRef.current = false;
      }
    };

    sync();
    // Re-run when bundle loads/changes OR when a (new) contentService becomes available
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bundleStatus?.isLoaded, bundleStatus?.version, contentService]);
};

/**
 * Call this on sign-out so that the next sign-in always re-runs the milestone
 * sync.  Without this, clearing localStorage and signing back in leaves milestone
 * storage empty because the module-level guard thinks the sync already ran.
 */
export const resetMilestoneSyncSession = () => {
  initialSyncDoneThisSession = false;
};
